import { useId, useState, type FormEvent } from 'react';

import { describeError } from '../../ui/problemDetails.ts';
import { AISLE_LABELS, OTHER, type Aisle } from './aisleLabels.ts';
import { useAisles, useDeleteStore, useRenameStore, useSetAisleOrder } from './useAisles.ts';
import { readSelectedStoreId } from './useSelectedStore.ts';

/**
 * The household's shops, and the order the aisles come in at each one.
 *
 * This used to be one order per group, on the reasoning that an order describes a building
 * rather than a shopping trip. That reasoning was right and is exactly why there is more
 * than one of them now: a household that shops at two supermarkets was walking the second
 * one in the first one's order.
 *
 * It lives in the Groups panel, even though everything about aisles is otherwise an item
 * concern, because a panel goes where its object is — the shops belong to the household.
 *
 * <b>Its selection is its own.</b> It starts on the shop this device has open, which is
 * usually the one somebody opened this panel to fix, and then stays where it is put: you can
 * arrange Aldi from the aisle you are standing in at Tesco without the list behind this
 * dialog resorting itself.
 *
 * <b>Up and down buttons rather than dragging.</b> HTML5 drag-and-drop does not fire on
 * touch at all, so a drag handle on a phone needs pointer events, a scroll lock and an
 * autoscroll — a few hundred lines and a dependency, for a control most households will
 * touch once. Two buttons per row work with a thumb, with a keyboard and with a screen
 * reader, and they say what they do.
 *
 * Offered to every member, not only the owner. It is a display preference rather than
 * administration, the server does not restrict it, and the person who does the shopping is
 * frequently not the person who created the group.
 */
export function StoreSettings({ groupId }: { groupId: string }) {
  const selectId = useId();
  const { stores, isPending } = useAisles(groupId);

  // Its own, seeded from the device's rather than subscribed to it — see the note above.
  const [chosenId, setChosenId] = useState<string | null>(() => readSelectedStoreId(groupId));
  const store = stores.find((candidate) => candidate.id === chosenId) ?? stores[0];

  const [name, setName] = useState<string | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const save = useSetAisleOrder(groupId, store?.id);
  const rename = useRenameStore(groupId);
  const remove = useDeleteStore(groupId);

  // `other` is in the order the list is drawn in, but it is not an aisle and cannot be
  // moved, so it is not in the one that can be arranged.
  const arrangeable = (store?.order ?? []).filter((aisle) => aisle !== OTHER);

  // Null means "not being edited", so the field follows the store as it is switched and as
  // the server's answer lands, rather than holding whatever was typed against another shop.
  const typed = name ?? store?.name ?? '';
  const trimmed = typed.trim();

  const error = save.error ?? rename.error ?? remove.error;

  function move(from: number, direction: -1 | 1) {
    const to = from + direction;
    const moved = arrangeable[from];
    const displaced = arrangeable[to];

    if (!moved || !displaced) {
      return;
    }

    const next: Aisle[] = [...arrangeable];
    next[from] = displaced;
    next[to] = moved;

    save.mutate(next);
  }

  function handleRename(event: FormEvent) {
    event.preventDefault();

    if (store && trimmed.length > 0 && trimmed !== store.name) {
      rename.mutate({ storeId: store.id, name: trimmed }, { onSuccess: () => setName(null) });
    }
  }

  function choose(storeId: string) {
    setChosenId(storeId);
    // Both belong to the shop that was showing, not to the one now selected. Leaving the
    // armed delete would let the second tap remove a shop the first tap was not aimed at.
    setName(null);
    setConfirmingDelete(false);
  }

  if (isPending) {
    return (
      <section className="settings-section">
        <h3 className="settings-section__title">Aisle order</h3>
        <p className="muted">Loading…</p>
      </section>
    );
  }

  return (
    <section className="settings-section">
      <h3 className="settings-section__title">Aisle order</h3>

      <p className="settings-hint">
        The order your shop is laid out in. Lists that are grouped by aisle follow it, and
        each person sees the shop they picked — so you can be in different ones at the same
        time.
      </p>

      {/* Hidden at one shop, like every other switcher in the app. */}
      {stores.length > 1 && (
        <div className="settings-row">
          <label className="visually-hidden" htmlFor={selectId}>
            Shop to arrange
          </label>
          <select
            id={selectId}
            className="settings-row__input"
            value={store?.id ?? ''}
            onChange={(event) => choose(event.target.value)}
          >
            {stores.map((candidate) => (
              <option key={candidate.id} value={candidate.id}>
                {candidate.name}
              </option>
            ))}
          </select>
        </div>
      )}

      <form className="settings-row" onSubmit={handleRename}>
        <input
          className="settings-row__input"
          value={typed}
          onChange={(event) => setName(event.target.value)}
          aria-label="Shop name"
          maxLength={80}
          required
        />
        <button
          className="button button--primary button--small"
          type="submit"
          disabled={rename.isPending || trimmed.length === 0 || trimmed === store?.name}
        >
          Save
        </button>
      </form>

      <ol className="aisle-order">
        {arrangeable.map((aisle, index) => (
          <li key={aisle} className="aisle-order__row">
            <span className="aisle-order__name">{AISLE_LABELS[aisle]}</span>

            <span className="aisle-order__moves">
              {/* Named for the aisle rather than for the direction. Eight rows of "Move
                  up" is eight identical controls to anyone reading the labels out. */}
              <button
                type="button"
                className="aisle-order__move"
                onClick={() => move(index, -1)}
                disabled={index === 0 || save.isPending}
                aria-label={`Move ${AISLE_LABELS[aisle]} earlier`}
              >
                <Chevron up />
              </button>

              <button
                type="button"
                className="aisle-order__move"
                onClick={() => move(index, 1)}
                disabled={index === arrangeable.length - 1 || save.isPending}
                aria-label={`Move ${AISLE_LABELS[aisle]} later`}
              >
                <Chevron />
              </button>
            </span>
          </li>
        ))}
      </ol>

      <p className="settings-hint">
        Anything the app can&rsquo;t place goes in {AISLE_LABELS[OTHER]}, at the end.
      </p>

      {/* The API refuses to remove a household's only shop; saying so is friendlier than
          letting someone press a button and read an error. */}
      {stores.length > 1 && store && (
        <div className="settings-actions">
          {confirmingDelete ? (
            <button
              type="button"
              className="link-button link-button--danger"
              onClick={() => remove.mutate(store.id, { onSuccess: () => setConfirmingDelete(false) })}
              disabled={remove.isPending}
            >
              Really remove “{store.name}” and the order it holds?
            </button>
          ) : (
            <button
              type="button"
              className="link-button link-button--danger"
              onClick={() => setConfirmingDelete(true)}
            >
              Remove shop
            </button>
          )}
        </div>
      )}

      {error && (
        <p className="form-error" role="alert">
          {describeError(error)}
        </p>
      )}
    </section>
  );
}

function Chevron({ up = false }: { up?: boolean }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width="18"
      height="18"
      aria-hidden="true"
      focusable="false"
      style={up ? undefined : { transform: 'rotate(180deg)' }}
    >
      <path
        d="M6 15l6-6 6 6"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
